import { useState } from 'react';
import styled from 'styled-components';

import Card from 'components/insights/layout/Card';
import Button from 'components/shared/Button';
import SegmentedControl from 'components/shared/SegmentedControl';
import { BulkPayerType, NetworkStatus, PAYER_TYPES } from 'models/GroupPayerNetworkStatus';
import { BodySmallBold, Label } from 'styles/typography';

import { countOf } from './facilityNetworkStatusColumns';

const PAYER_TYPE_OPTIONS = [
  { label: 'All Payer Types', value: 'all' },
  ...PAYER_TYPES.map(({ payerType, label }) => ({ label, value: payerType })),
];

// SegmentedControl values are strings, so Not set travels as 'not_set' until Apply.
const STATUS_OPTIONS = [
  { label: 'In Network', value: 'in' },
  { label: 'Out of Network', value: 'out' },
  { label: 'Not Set', value: 'not_set' },
];

type BulkEditPanelProps = {
  count: number;
  noun: [string, string];
  applying: boolean;
  onApply: (payerType: BulkPayerType, status: NetworkStatus) => void;
  onClear: () => void;
};

export default function BulkEditPanel({ count, noun, applying, onApply, onClear }: BulkEditPanelProps) {
  const [payerType, setPayerType] = useState('all');
  const [status, setStatus] = useState('in');

  return (
    <Panel data-testid='bulk-edit-panel'>
      <Body>
        <BodySmallBold>{`${countOf(count, ...noun)} Selected`}</BodySmallBold>
        <Field>
          <Label fontSize='12px'>Choose payer type</Label>
          <SegmentedControl options={PAYER_TYPE_OPTIONS} selectedValue={payerType} onChange={setPayerType} />
        </Field>
        <Field>
          <Label fontSize='12px'>Choose network status</Label>
          <SegmentedControl options={STATUS_OPTIONS} selectedValue={status} onChange={setStatus} />
        </Field>
      </Body>
      <Footer>
        <ClearButton variant='ghost' size='xsmall' onClick={onClear} disabled={applying}>
          Clear Selections
        </ClearButton>
        <ApplyButton
          variant='primary'
          size='xsmall'
          loading={applying}
          disabled={applying}
          onClick={() => onApply(payerType as BulkPayerType, status === 'not_set' ? null : (status as NetworkStatus))}>
          Apply Changes
        </ApplyButton>
      </Footer>
    </Panel>
  );
}

const Panel = styled(Card)`
  max-width: 650px;
  margin-bottom: 24px;
  padding: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 15px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
`;

// Figma's footer controls are smaller than the shared button sizes: a 101x24 button and bare text.
const ClearButton = styled(Button)`
  padding: 0;
  font-size: 10px;
  line-height: 13px;
  color: ${({ theme }) => theme.colors.red100};
`;

const ApplyButton = styled(Button)`
  padding: 3px 16px;
  font-size: 10px;
  line-height: 16px;
`;
